import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  FileSpreadsheet,
  FileUp,
  GitCompareArrows,
  Loader2,
  MapPin,
  ScanSearch,
  TableProperties,
  Upload,
  X,
} from 'lucide-react';
import { useToast } from '../../../toast';
import { getWasteApiErrorMessage } from '../services';
import {
  buildInitialFieldMappings,
  buildInitialValueMappings,
  buildSourceSnapshot,
  getFieldByKey,
  getIssueCounts,
  getRequiredFieldMappingErrors,
  getRequiredValueMappingErrors,
  getSourceModeLabel,
  parseImportFile,
  serializeFieldMappings,
  serializeValueMappings,
} from './waste-import-utils';
import { wasteImportsApi } from './waste-imports-api';
import type {
  FieldMappingState,
  ImportCoordinateColumns,
  ImportFieldDefinition,
  ImportPreviewIssue,
  ImportPreviewResponse,
  ImportQualityResolutions,
  ImportRunDetailsResponse,
  ImportRunReportResponse,
  ImportSourceMode,
  ParsedImportSource,
  SerializedImportableEntityDefinition,
  ValueMappingState,
  WasteImportStepId,
} from './types';
import './waste-import-wizard.css';

const WASTE_ENTITY_ORDER = ['waste_container', 'waste_depot'] as const;

const STEPS: Array<{
  id: WasteImportStepId;
  label: string;
  icon: React.ReactNode;
}> = [
  { id: 'file', label: 'Archivo', icon: <FileUp size={16} /> },
  { id: 'entity', label: 'Entidad', icon: <TableProperties size={16} /> },
  { id: 'geometry', label: 'Geometría', icon: <MapPin size={16} /> },
  { id: 'field-mapping', label: 'Campos', icon: <FileSpreadsheet size={16} /> },
  { id: 'value-matching', label: 'Valores', icon: <GitCompareArrows size={16} /> },
  { id: 'preview', label: 'Vista previa', icon: <ScanSearch size={16} /> },
  { id: 'result', label: 'Resultado', icon: <CheckCircle2 size={16} /> },
];

const DEFAULT_QUALITY_RESOLUTIONS: Required<ImportQualityResolutions> = {
  exactDuplicates: 'keep',
  spatialConflicts: 'keep',
  spatialProximity: 'keep',
};

export interface WasteImportWizardModalProps {
  isOpen: boolean;
  projectId: string | null;
  preferredEntityType?: string | null;
  onClose: () => void;
}

function formatIssueCategory(category: ImportPreviewIssue['category']): string {
  switch (category) {
    case 'fatal':
      return 'Crítico';
    case 'error':
      return 'Error';
    case 'warning':
      return 'Aviso';
    case 'default_applied':
      return 'Valor automático';
    case 'omitted':
      return 'Omitida';
    default:
      return category;
  }
}

function formatStatus(status: string): string {
  switch (status) {
    case 'valid':
      return 'Válida';
    case 'valid_with_warnings':
      return 'Válida con avisos';
    case 'invalid':
      return 'Inválida';
    case 'omitted':
      return 'Omitida';
    case 'imported':
      return 'Importada';
    case 'failed':
      return 'No importada';
    case 'committed':
      return 'Completada';
    case 'committed_with_errors':
      return 'Completada con incidencias';
    default:
      return status;
  }
}

function formatDataType(dataType: string): string {
  switch (dataType) {
    case 'string':
      return 'Texto';
    case 'number':
      return 'Número';
    case 'boolean':
      return 'Sí/No';
    case 'date':
      return 'Fecha';
    case 'enum':
      return 'Opción';
    case 'enum[]':
      return 'Lista de opciones';
    default:
      return dataType;
  }
}

function formatValueMatcherStrategy(strategy: string): string {
  switch (strategy) {
    case 'enum-map':
      return 'Relaciona los valores del archivo con las opciones disponibles en WESTEER.';
    case 'boolean-map':
      return 'Convierte respuestas como sí/no, activo/inactivo o equivalentes.';
    case 'numeric-scale-map':
      return 'Convierte valores numéricos a la escala esperada por WESTEER.';
    case 'split-list-map':
      return 'Separa listas del archivo y traduce cada valor al catálogo de WESTEER.';
    default:
      return strategy;
  }
}

function isEmptyDisplayValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

function getValueAtPath(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((currentValue, segment) => {
    if (
      typeof currentValue !== 'object' ||
      currentValue === null ||
      !(segment in currentValue)
    ) {
      return undefined;
    }

    return (currentValue as Record<string, unknown>)[segment];
  }, source);
}

function formatFieldValue(
  field: ImportFieldDefinition | null,
  value: unknown,
): string {
  if (isEmptyDisplayValue(value)) {
    return '—';
  }

  if (Array.isArray(value)) {
    return value.map((entry) => formatFieldValue(field, entry)).join(', ');
  }

  if (field?.dataType === 'boolean' || typeof value === 'boolean') {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return ['true', '1', 'si', 'sí', 'yes'].includes(normalized) ? 'Sí' : 'No';
    }
    return value ? 'Sí' : 'No';
  }

  if (field?.dataType === 'date' && typeof value === 'string') {
    return value.includes('T') ? value.slice(0, 10) : value;
  }

  if ((field?.dataType === 'enum' || field?.dataType === 'enum[]') && field.enumOptions) {
    const matchedOption = field.enumOptions.find((option) => option.value === value);
    if (matchedOption) {
      return matchedOption.label;
    }
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  }

  return String(value);
}

function formatMappedFields(
  mappedFields: Record<string, unknown> | null | undefined,
  definition: SerializedImportableEntityDefinition | null,
): string {
  if (!mappedFields) {
    return '—';
  }

  if (!definition) {
    const entries = Object.entries(mappedFields).slice(0, 3);
    if (entries.length === 0) {
      return '—';
    }

    return entries
      .map(
        ([key, value]) =>
          `${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`,
      )
      .join(' · ');
  }

  const visibleEntries = definition.fields
    .map((field) => ({
      field,
      value: getValueAtPath(mappedFields, field.targetPath),
    }))
    .filter((entry) => !isEmptyDisplayValue(entry.value))
    .slice(0, 4);

  if (visibleEntries.length === 0) {
    return '—';
  }

  return visibleEntries
    .map(({ field, value }) => `${field.label}: ${formatFieldValue(field, value)}`)
    .join(' · ');
}

function getFieldGuidance(field: ImportFieldDefinition): string {
  if (field.description) {
    return field.description;
  }

  if (field.defaultStrategy === 'service-generated') {
    return 'Si no viene informado, WESTEER lo genera automáticamente al guardar.';
  }

  if (field.defaultStrategy === 'fixed') {
    return 'Si no viene informado, WESTEER completa este dato automáticamente.';
  }

  switch (field.dataType) {
    case 'boolean':
      return 'Acepta respuestas equivalentes a sí o no.';
    case 'date':
      return 'Usa una fecha reconocible, idealmente año-mes-día.';
    case 'number':
      return 'Es un valor numérico, por ejemplo capacidad o nivel.';
    case 'enum':
      return 'Debe coincidir con una de las opciones disponibles en WESTEER.';
    case 'enum[]':
      return 'Puede contener varias opciones separadas.';
    default:
      return 'Se importa como texto.';
  }
}

function getFieldDefaultLabel(field: ImportFieldDefinition): string | null {
  if (field.defaultStrategy === 'service-generated') {
    return 'Se genera automáticamente';
  }

  if (field.defaultStrategy === 'fixed') {
    return 'Tiene valor automático';
  }

  return null;
}

function summarizeIssueMessages(
  issues: ImportPreviewIssue[],
  maxMessages = 2,
): string {
  const messages = issues
    .map((issue) => issue.message.trim())
    .filter(
      (message, index, allMessages) =>
        message.length > 0 && allMessages.indexOf(message) === index,
    )
    .slice(0, maxMessages);

  if (messages.length === 0) {
    return 'Sin observaciones';
  }

  return messages.join(' · ');
}

function formatIssueCodeLabel(issue: ImportPreviewIssue): string {
  switch (issue.code) {
    case 'duplicate_spatial_exact_in_batch':
      return 'Duplicado exacto en archivo';
    case 'duplicate_spatial_exact_existing':
      return 'Duplicado exacto contra proyecto';
    case 'spatial_conflict_in_batch':
      return 'Conflicto espacial en archivo';
    case 'spatial_conflict_existing':
      return 'Conflicto espacial contra proyecto';
    case 'spatial_proximity_in_batch':
      return 'Proximidad en archivo';
    case 'spatial_proximity_existing':
      return 'Proximidad contra proyecto';
    case 'geometry_outside_project_area':
      return 'Fuera del área del proyecto';
    default:
      return formatIssueCategory(issue.category);
  }
}

function getPriorityIssues(issues: ImportPreviewIssue[]): ImportPreviewIssue[] {
  const seenCodes = new Set<string>();

  return issues.filter((issue) => {
    if (
      ![
        'duplicate_spatial_exact_in_batch',
        'duplicate_spatial_exact_existing',
        'spatial_conflict_in_batch',
        'spatial_conflict_existing',
        'spatial_proximity_in_batch',
        'spatial_proximity_existing',
        'geometry_outside_project_area',
      ].includes(issue.code)
    ) {
      return false;
    }

    if (seenCodes.has(issue.code)) {
      return false;
    }

    seenCodes.add(issue.code);
    return true;
  });
}

function getSpatialQualitySummaryItems(
  summary:
    | {
        exactDuplicateInFileRows?: number;
        exactDuplicateAgainstProjectRows?: number;
        spatialConflictInFileRows?: number;
        spatialConflictAgainstProjectRows?: number;
        spatialProximityInFileRows?: number;
        spatialProximityAgainstProjectRows?: number;
        outsideProjectAreaRows?: number;
      }
    | null
    | undefined,
): Array<{ key: string; label: string; count: number; tone: 'danger' | 'warning' }> {
  return [
    {
      key: 'exact-file',
      label: 'Duplicado exacto en archivo',
      count: summary?.exactDuplicateInFileRows ?? 0,
      tone: 'danger',
    },
    {
      key: 'exact-project',
      label: 'Duplicado exacto contra proyecto',
      count: summary?.exactDuplicateAgainstProjectRows ?? 0,
      tone: 'danger',
    },
    {
      key: 'conflict-file',
      label: 'Conflicto espacial en archivo',
      count: summary?.spatialConflictInFileRows ?? 0,
      tone: 'warning',
    },
    {
      key: 'conflict-project',
      label: 'Conflicto espacial contra proyecto',
      count: summary?.spatialConflictAgainstProjectRows ?? 0,
      tone: 'warning',
    },
    {
      key: 'proximity-file',
      label: 'Proximidad en archivo',
      count: summary?.spatialProximityInFileRows ?? 0,
      tone: 'warning',
    },
    {
      key: 'proximity-project',
      label: 'Proximidad contra proyecto',
      count: summary?.spatialProximityAgainstProjectRows ?? 0,
      tone: 'warning',
    },
    {
      key: 'outside-area',
      label: 'Fuera del área del proyecto',
      count: summary?.outsideProjectAreaRows ?? 0,
      tone: 'danger',
    },
  ];
}

function formatEntityLabel(
  entityType: string,
  definitions: SerializedImportableEntityDefinition[],
  fallback = entityType,
): string {
  return definitions.find((definition) => definition.entityType === entityType)?.label ?? fallback;
}

function formatEntityPluralLabel(
  entityType: string,
  definitions: SerializedImportableEntityDefinition[],
): string {
  return (
    definitions.find((definition) => definition.entityType === entityType)?.labelPlural ??
    formatEntityLabel(entityType, definitions, entityType)
  );
}

function buildResultHeadline(
  summary: ImportRunDetailsResponse['summary'],
  entityLabelPlural: string,
): string {
  if (!summary) {
    return 'La importación finalizó.';
  }

  const importedRows = summary.importedRows;
  const rowsToReview = summary.invalidRows + summary.failedRows + summary.omittedRows;
  const normalizedEntityLabel = entityLabelPlural.toLowerCase();

  if (importedRows > 0 && rowsToReview === 0) {
    return `Se importaron ${importedRows} ${normalizedEntityLabel} sin filas pendientes.`;
  }

  if (importedRows > 0) {
    return `Se importaron ${importedRows} ${normalizedEntityLabel} y ${rowsToReview} fila(s) quedaron para revisar.`;
  }

  if (rowsToReview > 0) {
    return 'No se importó ninguna fila. Revisa el detalle antes de volver a intentar.';
  }

  return 'La importación terminó sin cambios.';
}

function getConstantPlaceholder(field: ImportFieldDefinition): string {
  switch (field.dataType) {
    case 'number':
      return 'Valor numérico';
    case 'boolean':
      return 'Sí / No';
    case 'date':
      return 'YYYY-MM-DD';
    default:
      return 'Valor constante';
  }
}

function renderConstantInput(
  field: ImportFieldDefinition,
  currentValue: unknown,
  onChange: (value: unknown) => void,
): JSX.Element {
  if (field.dataType === 'enum' && field.enumOptions) {
    return (
      <select
        className="waste-import-wizard__select"
        value={currentValue === undefined ? '' : String(currentValue)}
        onChange={(event) => onChange(event.target.value || undefined)}
      >
        <option value="">Seleccionar valor</option>
        {field.enumOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.dataType === 'boolean') {
    return (
      <select
        className="waste-import-wizard__select"
        value={
          currentValue === true ? 'true' : currentValue === false ? 'false' : ''
        }
        onChange={(event) => {
          if (event.target.value === '') {
            onChange(undefined);
            return;
          }
          onChange(event.target.value === 'true');
        }}
      >
        <option value="">Seleccionar valor</option>
        <option value="true">Sí</option>
        <option value="false">No</option>
      </select>
    );
  }

  return (
    <input
      className="waste-import-wizard__input"
      type={field.dataType === 'number' ? 'number' : field.dataType === 'date' ? 'date' : 'text'}
      value={currentValue === undefined || currentValue === null ? '' : String(currentValue)}
      placeholder={getConstantPlaceholder(field)}
      onChange={(event) => onChange(event.target.value || undefined)}
    />
  );
}

export function WasteImportWizardModal({
  isOpen,
  projectId,
  preferredEntityType = null,
  onClose,
}: WasteImportWizardModalProps): JSX.Element | null {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const [entities, setEntities] = useState<SerializedImportableEntityDefinition[]>([]);
  const [isEntitiesLoading, setIsEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);

  const [parsedSource, setParsedSource] = useState<ParsedImportSource | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null);
  const [geometryMode, setGeometryMode] = useState<ImportSourceMode | null>(null);
  const [coordinateColumns, setCoordinateColumns] = useState<ImportCoordinateColumns | null>(null);

  const [fieldMappings, setFieldMappings] = useState<FieldMappingState>({});
  const [valueMappings, setValueMappings] = useState<ValueMappingState>({});
  const [qualityResolutions, setQualityResolutions] = useState<Required<ImportQualityResolutions>>(
    () => ({ ...DEFAULT_QUALITY_RESOLUTIONS }),
  );

  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const [commitError, setCommitError] = useState<string | null>(null);
  const [isCommitLoading, setIsCommitLoading] = useState(false);
  const [runDetails, setRunDetails] = useState<ImportRunDetailsResponse | null>(null);
  const [runReport, setRunReport] = useState<ImportRunReportResponse | null>(null);

  const activeStep = STEPS[activeStepIndex];

  const supportedEntities = useMemo(
    () =>
      entities
        .filter(
          (entity) =>
            entity.enabled &&
            WASTE_ENTITY_ORDER.includes(
              entity.entityType as (typeof WASTE_ENTITY_ORDER)[number],
            ),
        )
        .sort(
          (left, right) =>
            WASTE_ENTITY_ORDER.indexOf(
              left.entityType as (typeof WASTE_ENTITY_ORDER)[number],
            ) -
            WASTE_ENTITY_ORDER.indexOf(
              right.entityType as (typeof WASTE_ENTITY_ORDER)[number],
            ),
        ),
    [entities],
  );

  const selectedDefinition = useMemo(
    () =>
      supportedEntities.find((entity) => entity.entityType === selectedEntityType) ?? null,
    [supportedEntities, selectedEntityType],
  );

  const availableSourceModes = useMemo(() => {
    if (!parsedSource || !selectedDefinition) {
      return [] as ImportSourceMode[];
    }

    return parsedSource.geometryAnalysis.availableModes.filter((mode) =>
      selectedDefinition.supportedSourceModes.includes(mode),
    );
  }, [parsedSource, selectedDefinition]);

  const serializedFieldMappings = useMemo(
    () =>
      selectedDefinition
        ? serializeFieldMappings(selectedDefinition, fieldMappings)
        : [],
    [fieldMappings, selectedDefinition],
  );

  const serializedValueMappings = useMemo(
    () => serializeValueMappings(valueMappings),
    [valueMappings],
  );

  const fieldMappingErrors = useMemo(() => {
    if (!selectedDefinition) {
      return [];
    }
    return getRequiredFieldMappingErrors(selectedDefinition, fieldMappings);
  }, [fieldMappings, selectedDefinition]);

  const valueMappingErrors = useMemo(() => {
    if (!selectedDefinition || !parsedSource) {
      return [];
    }
    return getRequiredValueMappingErrors(
      selectedDefinition,
      parsedSource,
      fieldMappings,
      valueMappings,
    );
  }, [fieldMappings, parsedSource, selectedDefinition, valueMappings]);

  const applicableValueMatchers = useMemo(() => {
    if (!selectedDefinition) {
      return [];
    }

    return selectedDefinition.valueMatchers.filter((matcher) => {
      const mapping = fieldMappings[matcher.fieldKey];
      return mapping?.mode === 'column';
    });
  }, [fieldMappings, selectedDefinition]);

  const previewRows = preview?.rows ?? [];
  const resultRows = runReport?.rows ?? [];
  const activeDefinition =
    selectedDefinition ??
    (preview?.definition as SerializedImportableEntityDefinition | undefined) ??
    (runDetails
      ? supportedEntities.find((entity) => entity.entityType === runDetails.entityType) ?? null
      : null) ??
    null;
  const activeFileName = parsedSource?.fileName ?? runDetails?.source.fileName ?? null;
  const activeEntityLabel =
    selectedDefinition?.label ??
    (runDetails ? formatEntityLabel(runDetails.entityType, supportedEntities) : null);
  const activeResultEntityPluralLabel = runDetails
    ? formatEntityPluralLabel(runDetails.entityType, supportedEntities)
    : activeDefinition?.labelPlural ?? 'registros';

  const previewStatusCounts = useMemo(
    () =>
      previewRows.reduce<Record<string, number>>((accumulator, row) => {
        accumulator[row.status] = (accumulator[row.status] ?? 0) + 1;
        return accumulator;
      }, {}),
    [previewRows],
  );

  const resultStatusCounts = useMemo(
    () =>
      resultRows.reduce<Record<string, number>>((accumulator, row) => {
        accumulator[row.status] = (accumulator[row.status] ?? 0) + 1;
        return accumulator;
      }, {}),
    [resultRows],
  );

  const resultRowsToReview = useMemo(
    () =>
      resultRows.filter(
        (row) =>
          row.status === 'invalid' || row.status === 'failed' || row.status === 'omitted',
      ),
    [resultRows],
  );

  const resultRowsWithWarnings = useMemo(
    () =>
      resultRows.filter((row) =>
        row.issues.some((issue) => issue.category === 'warning'),
      ),
    [resultRows],
  );

  const resultRowsWithDefaults = useMemo(
    () =>
      resultRows.filter((row) =>
        row.issues.some((issue) => issue.category === 'default_applied'),
      ),
    [resultRows],
  );

  const previewSpatialQualityItems = useMemo(
    () => getSpatialQualitySummaryItems(preview?.summary),
    [preview?.summary],
  );

  const resultSpatialQualityItems = useMemo(
    () => getSpatialQualitySummaryItems(runDetails?.summary),
    [runDetails?.summary],
  );

  const previewResolutionItems = useMemo(
    () => [
      {
        key: 'exactDuplicates' as const,
        label: 'Duplicados exactos',
        description:
          'Incluye coincidencias exactas dentro del archivo y contra el proyecto. Por defecto quedan inválidas.',
        count:
          (preview?.summary.exactDuplicateInFileRows ?? 0) +
          (preview?.summary.exactDuplicateAgainstProjectRows ?? 0),
        action: qualityResolutions.exactDuplicates,
        keepLabel: 'Dejar como inválidos',
        omitLabel: 'Omitir todos',
      },
      {
        key: 'spatialConflicts' as const,
        label: 'Conflictos espaciales',
        description:
          'Misma ubicación exacta con atributos relevantes distintos. Por defecto se importan con aviso.',
        count:
          (preview?.summary.spatialConflictInFileRows ?? 0) +
          (preview?.summary.spatialConflictAgainstProjectRows ?? 0),
        action: qualityResolutions.spatialConflicts,
        keepLabel: 'Importar con aviso',
        omitLabel: 'Omitir todos',
      },
      {
        key: 'spatialProximity' as const,
        label: 'Proximidades',
        description:
          'Puntos a pocos metros entre sí o cerca de entidades ya cargadas. Por defecto se importan con aviso.',
        count:
          (preview?.summary.spatialProximityInFileRows ?? 0) +
          (preview?.summary.spatialProximityAgainstProjectRows ?? 0),
        action: qualityResolutions.spatialProximity,
        keepLabel: 'Importar con aviso',
        omitLabel: 'Omitir todos',
      },
    ],
    [preview?.summary, qualityResolutions],
  );

  const resetState = (): void => {
    setActiveStepIndex(0);
    setIsDragging(false);
    setParsedSource(null);
    setParseError(null);
    setSelectedEntityType(null);
    setGeometryMode(null);
    setCoordinateColumns(null);
    setFieldMappings({});
    setValueMappings({});
    setQualityResolutions({ ...DEFAULT_QUALITY_RESOLUTIONS });
    setPreview(null);
    setPreviewError(null);
    setCommitError(null);
    setRunDetails(null);
    setRunReport(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setIsEntitiesLoading(true);
    setEntitiesError(null);

    void wasteImportsApi
      .getEntities()
      .then((response) => {
        if (!cancelled) {
          setEntities(response);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          const message = getWasteApiErrorMessage(error);
          setEntitiesError(message);
          showToast('error', message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsEntitiesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, showToast]);

  useEffect(() => {
    if (!parsedSource || !selectedDefinition) {
      return;
    }

    const nextCoordinateColumns =
      parsedSource.geometryAnalysis.suggestedCoordinateColumns;

    setCoordinateColumns(nextCoordinateColumns);
    setGeometryMode((currentMode) => {
      if (currentMode && availableSourceModes.includes(currentMode)) {
        return currentMode;
      }

      if (availableSourceModes.includes('feature-geometry')) {
        return 'feature-geometry';
      }

      return availableSourceModes[0] ?? null;
    });
    setFieldMappings(buildInitialFieldMappings(selectedDefinition, parsedSource));
  }, [availableSourceModes, parsedSource, selectedDefinition]);

  useEffect(() => {
    if (!parsedSource || !selectedDefinition) {
      setValueMappings({});
      return;
    }

    const autoMappings = buildInitialValueMappings(
      selectedDefinition,
      parsedSource,
      fieldMappings,
    );

    setValueMappings((currentValueMappings) => {
      const merged: ValueMappingState = { ...autoMappings };

      for (const [fieldKey, nextEntry] of Object.entries(autoMappings)) {
        const currentEntry = currentValueMappings[fieldKey];
        if (!currentEntry) {
          continue;
        }

        merged[fieldKey] = {
          ...nextEntry,
          separator: currentEntry.separator ?? nextEntry.separator,
          mappings: nextEntry.mappings.map((nextMapping) => {
            const existingMapping = currentEntry.mappings.find(
              (candidate) => candidate.sourceValue === nextMapping.sourceValue,
            );

            return existingMapping && existingMapping.targetValue !== undefined
              ? existingMapping
              : nextMapping;
          }),
        };
      }

      return merged;
    });
  }, [fieldMappings, parsedSource, selectedDefinition]);

  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    setCommitError(null);
    setRunDetails(null);
    setRunReport(null);
  }, [selectedEntityType, geometryMode, coordinateColumns, fieldMappings, valueMappings, parsedSource]);

  useEffect(() => {
    setQualityResolutions({ ...DEFAULT_QUALITY_RESOLUTIONS });
  }, [selectedEntityType, parsedSource?.fileName]);

  useEffect(() => {
    if (!isOpen || !preferredEntityType || supportedEntities.length === 0) {
      return;
    }

    const preferredEntity = supportedEntities.find(
      (entity) => entity.entityType === preferredEntityType,
    );

    if (preferredEntity) {
      setSelectedEntityType(preferredEntity.entityType);
    }
  }, [isOpen, preferredEntityType, supportedEntities]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isPreviewLoading && !isCommitLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isCommitLoading, isOpen, isPreviewLoading, onClose]);

  const handleFile = async (file: File): Promise<void> => {
    setIsParsing(true);
    setParseError(null);

    try {
      const parsed = await parseImportFile(file);
      setParsedSource(parsed);
      setActiveStepIndex(1);
      if (parsed.warnings.length > 0) {
        showToast('warning', parsed.warnings[0]);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'No se pudo procesar el archivo';
      setParseError(message);
      showToast('error', message);
    } finally {
      setIsParsing(false);
    }
  };

  const handleFieldMappingChange = (
    fieldKey: string,
    nextMapping: Partial<Omit<FieldMappingState[string], 'fieldKey'>>,
  ): void => {
    setFieldMappings((currentState) => {
      const currentEntry = currentState[fieldKey];

      return {
        ...currentState,
        [fieldKey]: {
          ...(currentEntry ?? { fieldKey, mode: 'ignore' as const }),
          ...nextMapping,
          fieldKey,
        },
      };
    });
  };

  const handleValueMappingChange = (
    fieldKey: string,
    sourceValue: string,
    targetValue: unknown,
  ): void => {
    setValueMappings((currentState) => {
      const currentEntry = currentState[fieldKey];
      if (!currentEntry) {
        return currentState;
      }

      return {
        ...currentState,
        [fieldKey]: {
          ...currentEntry,
          mappings: currentEntry.mappings.map((entry) =>
            entry.sourceValue === sourceValue
              ? { ...entry, targetValue }
              : entry,
          ),
        },
      };
    });
  };

  const handleValueMappingSeparatorChange = (
    fieldKey: string,
    separator: string,
  ): void => {
    setValueMappings((currentState) => {
      const currentEntry = currentState[fieldKey];
      if (!currentEntry) {
        return currentState;
      }

      return {
        ...currentState,
        [fieldKey]: {
          ...currentEntry,
          separator,
        },
      };
    });
  };

  const buildPreviewPayload = (
    resolutionsOverride?: Required<ImportQualityResolutions>,
  ) => {
    if (!projectId || !parsedSource || !selectedDefinition || !geometryMode) {
      return null;
    }

    return {
      projectId,
      moduleId: 'waste',
      entityType: selectedDefinition.entityType,
      source: buildSourceSnapshot(parsedSource, geometryMode, coordinateColumns),
      fieldMappings: serializedFieldMappings,
      valueMappings: serializedValueMappings,
      qualityResolutions: resolutionsOverride ?? qualityResolutions,
    };
  };

  const runPreview = async (
    resolutionsOverride?: Required<ImportQualityResolutions>,
  ): Promise<boolean> => {
    if (!projectId) {
      const message = 'Selecciona un proyecto antes de importar.';
      setPreviewError(message);
      showToast('error', message);
      return false;
    }

    if (!parsedSource || !selectedDefinition || !geometryMode) {
      const message = 'Faltan datos para generar la vista previa.';
      setPreviewError(message);
      showToast('error', message);
      return false;
    }

    if (fieldMappingErrors.length > 0) {
      setPreviewError(fieldMappingErrors[0]);
      showToast('error', fieldMappingErrors[0]);
      return false;
    }

    if (
      geometryMode === 'coordinate-columns' &&
      (!coordinateColumns?.lng || !coordinateColumns?.lat)
    ) {
      const message = 'Debes seleccionar columnas válidas para longitud y latitud.';
      setPreviewError(message);
      showToast('error', message);
      return false;
    }

    if (valueMappingErrors.length > 0) {
      setPreviewError(valueMappingErrors[0]);
      showToast('error', valueMappingErrors[0]);
      return false;
    }

    setIsPreviewLoading(true);
    setPreviewError(null);

    try {
      const payload = buildPreviewPayload(resolutionsOverride);
      if (!payload) {
        throw new Error('Faltan datos para generar la vista previa.');
      }

      const response = await wasteImportsApi.preview(payload);
      setPreview(response);
      setActiveStepIndex(5);
      return true;
    } catch (error) {
      const message = getWasteApiErrorMessage(error);
      setPreviewError(message);
      showToast('error', message);
      return false;
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const runCommit = async (): Promise<void> => {
    if (!projectId || !parsedSource || !selectedDefinition || !geometryMode || !preview) {
      return;
    }

    setIsCommitLoading(true);
    setCommitError(null);

    try {
      const previewPayload = buildPreviewPayload();
      if (!previewPayload) {
        throw new Error('Faltan datos para confirmar la importación.');
      }

      const commitResponse = await wasteImportsApi.commit({
        ...previewPayload,
        previewFingerprint: preview.previewFingerprint,
      });

      if (commitResponse.summary.importedRows > 0) {
        if (selectedDefinition.entityType === 'waste_container') {
          window.dispatchEvent(new CustomEvent('container-created'));
        }
        if (selectedDefinition.entityType === 'waste_depot') {
          window.dispatchEvent(new CustomEvent('depot-created'));
        }
      }

      try {
        const [details, report] = await Promise.all([
          wasteImportsApi.getRun(commitResponse.runId),
          wasteImportsApi.getRunReport(commitResponse.runId),
        ]);
        setRunDetails(details);
        setRunReport(report);
      } catch (error) {
        console.warn('[WasteImportWizard] No se pudo recargar la corrida persistida:', error);
      }

      if (!runDetails) {
        setRunDetails((currentRunDetails) =>
          currentRunDetails ?? {
            runId: commitResponse.runId,
            projectId,
            moduleId: 'waste',
            entityType: selectedDefinition.entityType,
            status: commitResponse.status,
            summary: commitResponse.summary,
            previewFingerprint: preview.previewFingerprint,
            definitionVersion: preview.definition.version,
            source: {
              format: parsedSource.format,
              fileName: parsedSource.fileName,
              rowCount: parsedSource.rows.length,
            },
            fieldMappings: serializedFieldMappings,
            valueMappings: serializedValueMappings,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        );
      }

      if (!runReport) {
        setRunReport((currentRunReport) =>
          currentRunReport ?? {
            runId: commitResponse.runId,
            status: commitResponse.status,
            rows: commitResponse.rows.map((row) => ({
              sourceRowIndex: row.sourceRowIndex,
              status: row.status,
              issues: row.issues,
              mappedFields: row.mappedFields ?? null,
              persistedEntityId: row.persistedEntityId ?? null,
              persistedCode: row.persistedCode ?? null,
            })),
          },
        );
      }

      showToast(
        commitResponse.summary.failedRows > 0 || commitResponse.summary.invalidRows > 0
          ? 'warning'
          : 'success',
        `Importación finalizada: ${commitResponse.summary.importedRows} fila(s) importada(s).`,
      );
      setActiveStepIndex(6);
    } catch (error) {
      const message = getWasteApiErrorMessage(error);
      setCommitError(message);
      showToast('error', message);
    } finally {
      setIsCommitLoading(false);
    }
  };

  const handleQualityResolutionChange = async (
    resolutionKey: keyof ImportQualityResolutions,
    value: 'keep' | 'omit',
  ): Promise<void> => {
    const nextResolutions = {
      ...qualityResolutions,
      [resolutionKey]: value,
    };

    setQualityResolutions(nextResolutions);

    if (preview) {
      await runPreview(nextResolutions);
    }
  };

  const handleNext = async (): Promise<void> => {
    if (activeStep.id === 'value-matching') {
      await runPreview();
      return;
    }

    if (activeStep.id === 'preview') {
      await runCommit();
      return;
    }

    setActiveStepIndex((currentStepIndex) =>
      Math.min(currentStepIndex + 1, STEPS.length - 1),
    );
  };

  const handleBack = (): void => {
    setActiveStepIndex((currentStepIndex) => Math.max(currentStepIndex - 1, 0));
  };

  const canGoNext = useMemo(() => {
    switch (activeStep.id) {
      case 'file':
        return parsedSource !== null && !isParsing;
      case 'entity':
        return selectedDefinition !== null;
      case 'geometry':
        return (
          geometryMode !== null &&
          (geometryMode !== 'coordinate-columns' ||
            (coordinateColumns?.lng && coordinateColumns?.lat))
        );
      case 'field-mapping':
        return fieldMappingErrors.length === 0;
      case 'value-matching':
        return valueMappingErrors.length === 0 && !isPreviewLoading;
      case 'preview':
        return !isCommitLoading && Boolean(preview && preview.summary.validRows > 0);
      case 'result':
        return true;
      default:
        return false;
    }
  }, [
    activeStep.id,
    coordinateColumns?.lat,
    coordinateColumns?.lng,
    fieldMappingErrors.length,
    geometryMode,
    isCommitLoading,
    isParsing,
    isPreviewLoading,
    parsedSource,
    preview,
    selectedDefinition,
    valueMappingErrors.length,
  ]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="waste-import-wizard__overlay"
      onClick={() => {
        if (!isPreviewLoading && !isCommitLoading) {
          onClose();
        }
      }}
    >
      <div
        className="waste-import-wizard"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="waste-import-wizard__header">
          <div>
            <p className="waste-import-wizard__eyebrow">Waste Import MVP</p>
            <h2>Importación de puntos</h2>
            <p className="waste-import-wizard__subtitle">
              Revisa el archivo, valida los campos y confirma antes de importar.
            </p>
          </div>

          <button
            type="button"
            className="waste-import-wizard__close"
            onClick={onClose}
            disabled={isPreviewLoading || isCommitLoading}
            aria-label="Cerrar importador"
          >
            <X size={18} />
          </button>
        </div>

        <div className="waste-import-wizard__steps">
          {STEPS.map((step, stepIndex) => (
            <div
              key={step.id}
              className={[
                'waste-import-wizard__step',
                stepIndex === activeStepIndex ? 'is-active' : '',
                stepIndex < activeStepIndex ? 'is-complete' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="waste-import-wizard__step-icon">{step.icon}</span>
              <span>{step.label}</span>
            </div>
          ))}
        </div>

        {(activeFileName || activeEntityLabel || geometryMode) && (
          <div className="waste-import-wizard__context-bar">
            {activeFileName && (
              <div className="waste-import-wizard__context-pill">
                <span className="waste-import-wizard__context-label">Archivo</span>
                <strong className="waste-import-wizard__context-value">{activeFileName}</strong>
              </div>
            )}
            {activeEntityLabel && (
              <div className="waste-import-wizard__context-pill">
                <span className="waste-import-wizard__context-label">Entidad</span>
                <strong className="waste-import-wizard__context-value">{activeEntityLabel}</strong>
              </div>
            )}
            {geometryMode && activeStep.id !== 'file' && activeStep.id !== 'entity' && (
              <div className="waste-import-wizard__context-pill">
                <span className="waste-import-wizard__context-label">Ubicación</span>
                <strong className="waste-import-wizard__context-value">
                  {getSourceModeLabel(geometryMode)}
                </strong>
              </div>
            )}
          </div>
        )}

        <div className="waste-import-wizard__body">
          {activeStep.id === 'file' && (
            <section className="waste-import-wizard__section">
              <div
                className={[
                  'waste-import-wizard__dropzone',
                  isDragging ? 'is-dragging' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDragging(false);
                  const droppedFile = event.dataTransfer.files?.[0];
                  if (droppedFile) {
                    void handleFile(droppedFile);
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={28} />
                <div>
                  <h3>Sube un CSV, GeoJSON o KML</h3>
                  <p>
                    Primero se analiza el archivo para mostrarte validaciones y vista
                    previa antes de guardar cambios.
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.geojson,.json,.kml"
                  onChange={(event) => {
                    const selectedFile = event.target.files?.[0];
                    if (selectedFile) {
                      void handleFile(selectedFile);
                    }
                  }}
                  hidden
                />
              </div>

              {isParsing && (
                <div className="waste-import-wizard__info-banner">
                  <Loader2 size={16} className="waste-import-wizard__spin" />
                  Procesando archivo localmente...
                </div>
              )}

              {parseError && (
                <div className="waste-import-wizard__error-banner">{parseError}</div>
              )}

              {parsedSource && (
                <div className="waste-import-wizard__grid">
                  <div className="waste-import-wizard__card">
                    <h3>Archivo seleccionado</h3>
                    <dl className="waste-import-wizard__summary-list">
                      <div>
                        <dt>Archivo</dt>
                        <dd>{parsedSource.fileName}</dd>
                      </div>
                      <div>
                        <dt>Formato</dt>
                        <dd>{parsedSource.format.toUpperCase()}</dd>
                      </div>
                      <div>
                        <dt>Filas</dt>
                        <dd>{parsedSource.rows.length}</dd>
                      </div>
                      <div>
                        <dt>Columnas</dt>
                        <dd>{parsedSource.columns.length}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="waste-import-wizard__card">
                    <h3>Ubicaciones detectadas</h3>
                    <dl className="waste-import-wizard__summary-list">
                      <div>
                        <dt>Cómo puede leerse la ubicación</dt>
                        <dd>
                          {parsedSource.geometryAnalysis.availableModes.length > 0
                            ? parsedSource.geometryAnalysis.availableModes
                                .map(getSourceModeLabel)
                                .join(' · ')
                          : 'Sin geometría utilizable'}
                        </dd>
                      </div>
                      <div>
                        <dt>Puntos detectados</dt>
                        <dd>{parsedSource.geometryAnalysis.pointGeometryRows}</dd>
                      </div>
                      <div>
                        <dt>Geometrías no puntuales</dt>
                        <dd>{parsedSource.geometryAnalysis.nonPointGeometryRows}</dd>
                      </div>
                      <div>
                        <dt>Sin geometría importable</dt>
                        <dd>{parsedSource.geometryAnalysis.missingGeometryRows}</dd>
                      </div>
                      <div>
                        <dt>Coordenadas sugeridas</dt>
                        <dd>
                          {parsedSource.geometryAnalysis.suggestedCoordinateColumns
                            ? `${parsedSource.geometryAnalysis.suggestedCoordinateColumns.lng} / ${parsedSource.geometryAnalysis.suggestedCoordinateColumns.lat}`
                            : 'No detectadas'}
                        </dd>
                      </div>
                    </dl>
                    <p className="waste-import-wizard__helper-text">
                      &quot;Sin geometría importable&quot; incluye filas o placemarks que no traen
                      un punto utilizable para ubicarlos en el mapa.
                    </p>
                  </div>
                </div>
              )}

              {parsedSource && parsedSource.warnings.length > 0 && (
                <div className="waste-import-wizard__card">
                  <h3>Observaciones del archivo</h3>
                  <ul className="waste-import-wizard__list">
                    {parsedSource.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {parsedSource && (
                <div className="waste-import-wizard__card">
                  <h3>Columnas encontradas</h3>
                  <div className="waste-import-wizard__column-list">
                    {parsedSource.columns.map((column) => (
                      <div key={column.key} className="waste-import-wizard__column-chip">
                        <strong>{column.key}</strong>
                        <span>{formatDataType(column.guessedType)}</span>
                        <small>
                          {column.sampleValues.length > 0
                            ? column.sampleValues.join(' · ')
                            : 'Sin muestra'}
                          {column.emptyCount > 0 ? ` · ${column.emptyCount} vacía(s)` : ''}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {activeStep.id === 'entity' && (
            <section className="waste-import-wizard__section">
              {isEntitiesLoading && (
                <div className="waste-import-wizard__info-banner">
                  <Loader2 size={16} className="waste-import-wizard__spin" />
                  Cargando definiciones importables...
                </div>
              )}

              {entitiesError && (
                <div className="waste-import-wizard__error-banner">{entitiesError}</div>
              )}

              <div className="waste-import-wizard__entity-grid">
                {supportedEntities.map((entity) => (
                  <button
                    key={entity.entityType}
                    type="button"
                    className={[
                      'waste-import-wizard__entity-card',
                      selectedEntityType === entity.entityType ? 'is-selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedEntityType(entity.entityType)}
                  >
                    <span className="waste-import-wizard__entity-icon">{entity.icon}</span>
                    <strong>{entity.label}</strong>
                    <span>{entity.labelPlural}</span>
                    <small>
                      {entity.fields.filter((field) => field.required).length} campo(s)
                      obligatorio(s) · importación puntual
                    </small>
                  </button>
                ))}
              </div>
            </section>
          )}

          {activeStep.id === 'geometry' && (
            <section className="waste-import-wizard__section">
              <div className="waste-import-wizard__grid">
                <div className="waste-import-wizard__card">
                  <h3>Cómo tomar la ubicación</h3>
                  <div className="waste-import-wizard__mode-grid">
                    {availableSourceModes.map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        className={[
                          'waste-import-wizard__mode-card',
                          geometryMode === mode ? 'is-selected' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setGeometryMode(mode)}
                      >
                        <strong>{getSourceModeLabel(mode)}</strong>
                        <span>
                          {mode === 'feature-geometry'
                            ? 'Usa la geometría del archivo tal como viene.'
                            : 'Construye Point desde dos columnas del archivo.'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="waste-import-wizard__card">
                  <h3>Qué trae el archivo</h3>
                  <dl className="waste-import-wizard__summary-list">
                    <div>
                      <dt>Tipos detectados</dt>
                      <dd>
                        {parsedSource?.geometryAnalysis.geometryKinds.length
                          ? parsedSource.geometryAnalysis.geometryKinds.join(' · ')
                          : 'Sin geometría embebida'}
                      </dd>
                    </div>
                    <div>
                      <dt>Puntos listos para importar</dt>
                      <dd>{parsedSource?.geometryAnalysis.pointGeometryRows ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Geometrías no puntuales</dt>
                      <dd>{parsedSource?.geometryAnalysis.nonPointGeometryRows ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Sin geometría importable</dt>
                      <dd>{parsedSource?.geometryAnalysis.missingGeometryRows ?? 0}</dd>
                    </div>
                  </dl>
                  <p className="waste-import-wizard__helper-text">
                    Solo se importan puntos. Las geometrías no puntuales o sin punto usable
                    quedan fuera hasta corregir el archivo.
                  </p>
                </div>
              </div>

              {geometryMode === 'coordinate-columns' && parsedSource && (
                <div className="waste-import-wizard__card">
                  <h3>Columnas para longitud y latitud</h3>
                  <div className="waste-import-wizard__coordinate-grid">
                    <label>
                      <span>Longitud / X</span>
                      <select
                        className="waste-import-wizard__select"
                        value={coordinateColumns?.lng ?? ''}
                        onChange={(event) =>
                          setCoordinateColumns((currentValue) => ({
                            lng: event.target.value,
                            lat: currentValue?.lat ?? '',
                          }))
                        }
                      >
                        <option value="">Seleccionar columna</option>
                        {parsedSource.columns.map((column) => (
                          <option key={column.key} value={column.key}>
                            {column.key}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label>
                      <span>Latitud / Y</span>
                      <select
                        className="waste-import-wizard__select"
                        value={coordinateColumns?.lat ?? ''}
                        onChange={(event) =>
                          setCoordinateColumns((currentValue) => ({
                            lng: currentValue?.lng ?? '',
                            lat: event.target.value,
                          }))
                        }
                      >
                        <option value="">Seleccionar columna</option>
                        {parsedSource.columns.map((column) => (
                          <option key={column.key} value={column.key}>
                            {column.key}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeStep.id === 'field-mapping' && selectedDefinition && parsedSource && (
            <section className="waste-import-wizard__section">
              {fieldMappingErrors.length > 0 && (
                <div className="waste-import-wizard__error-banner">
                  {fieldMappingErrors[0]}
                </div>
              )}

              <div className="waste-import-wizard__table">
                <div className="waste-import-wizard__table-head">
                  <span>Campo en WESTEER</span>
                  <span>Cómo completarlo</span>
                  <span>Dato del archivo</span>
                  <span>Ejemplo</span>
                </div>

                {selectedDefinition.fields.map((field) => {
                  const mapping = fieldMappings[field.key] ?? {
                    fieldKey: field.key,
                    mode: 'ignore',
                  };
                  const selectedColumn = parsedSource.columns.find(
                    (column) => column.key === mapping.sourceColumnKey,
                  );

                  return (
                    <div
                      key={field.key}
                      className="waste-import-wizard__table-row"
                    >
                      <div>
                        <strong>{field.label}</strong>
                        <p className="waste-import-wizard__field-description">
                          {getFieldGuidance(field)}
                        </p>
                        <div className="waste-import-wizard__field-meta">
                          <span>{formatDataType(field.dataType)}</span>
                          {field.required && <span className="is-required">obligatorio</span>}
                          {getFieldDefaultLabel(field) && (
                            <span>{getFieldDefaultLabel(field)}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <select
                          className="waste-import-wizard__select"
                          value={mapping.mode}
                          onChange={(event) => {
                            const nextMode = event.target.value as FieldMappingState[string]['mode'];
                            handleFieldMappingChange(field.key, {
                              mode: nextMode,
                              sourceColumnKey:
                                nextMode === 'column' ? mapping.sourceColumnKey : undefined,
                              constantValue:
                                nextMode === 'constant' ? mapping.constantValue : undefined,
                            });
                          }}
                        >
                          <option value="ignore">No importar</option>
                          <option value="column">Tomar columna</option>
                          {field.allowConstant && (
                            <option value="constant">Usar valor fijo</option>
                          )}
                        </select>
                      </div>

                      <div>
                        {mapping.mode === 'column' && (
                          <select
                            className="waste-import-wizard__select"
                            value={mapping.sourceColumnKey ?? ''}
                            onChange={(event) =>
                              handleFieldMappingChange(field.key, {
                                sourceColumnKey: event.target.value || undefined,
                              })
                            }
                          >
                            <option value="">Seleccionar columna</option>
                            {parsedSource.columns.map((column) => (
                              <option key={column.key} value={column.key}>
                                {column.key}
                              </option>
                            ))}
                          </select>
                        )}

                        {mapping.mode === 'constant' &&
                          renderConstantInput(
                            field,
                            mapping.constantValue,
                            (nextValue) =>
                              handleFieldMappingChange(field.key, {
                                constantValue: nextValue,
                              }),
                          )}

                        {mapping.mode === 'ignore' && (
                          <span className="waste-import-wizard__muted">No se importará</span>
                        )}
                      </div>

                      <div>
                        {mapping.mode === 'column' && selectedColumn ? (
                          <div className="waste-import-wizard__sample-block">
                            <span>{selectedColumn.sampleValues.join(' · ') || 'Sin muestra'}</span>
                            <small>
                              Tipo estimado: {formatDataType(selectedColumn.guessedType)}
                            </small>
                          </div>
                        ) : mapping.mode === 'constant' ? (
                          <span className="waste-import-wizard__muted">
                            Se aplicará el mismo valor a todas las filas
                          </span>
                        ) : (
                          <span className="waste-import-wizard__muted">—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {activeStep.id === 'value-matching' && selectedDefinition && parsedSource && (
            <section className="waste-import-wizard__section">
              {valueMappingErrors.length > 0 && (
                <div className="waste-import-wizard__error-banner">
                  {valueMappingErrors[0]}
                </div>
              )}

              {applicableValueMatchers.length === 0 ? (
                <div className="waste-import-wizard__empty-state">
                  <CheckCircle2 size={24} />
                  <div>
                    <strong>No hay traducciones de valores pendientes.</strong>
                    <p>Los campos mapeados no necesitan convertir valores del archivo.</p>
                  </div>
                </div>
              ) : (
                applicableValueMatchers.map((matcher) => {
                  const field = getFieldByKey(selectedDefinition, matcher.fieldKey);
                  const mappingState = valueMappings[matcher.fieldKey];
                  if (!field || !mappingState) {
                    return null;
                  }

                  return (
                    <div key={matcher.fieldKey} className="waste-import-wizard__card">
                      <div className="waste-import-wizard__card-header">
                        <div>
                          <h3>{field.label}</h3>
                          <p>{formatValueMatcherStrategy(matcher.strategy)}</p>
                        </div>

                        {matcher.strategy === 'split-list-map' && (
                          <label className="waste-import-wizard__separator-field">
                            <span>Separador</span>
                            <input
                              className="waste-import-wizard__input"
                              value={mappingState.separator ?? matcher.separator ?? ','}
                              onChange={(event) =>
                                handleValueMappingSeparatorChange(
                                  matcher.fieldKey,
                                  event.target.value,
                                )
                              }
                            />
                          </label>
                        )}
                      </div>

                      <div className="waste-import-wizard__table">
                        <div className="waste-import-wizard__table-head">
                          <span>Valor del archivo</span>
                          <span>Valor en WESTEER</span>
                          <span>Estado</span>
                        </div>

                        {mappingState.mappings.map((entry) => (
                          <div
                            key={`${matcher.fieldKey}-${entry.sourceValue}`}
                            className="waste-import-wizard__table-row"
                          >
                            <div>
                              <strong>{entry.sourceValue}</strong>
                            </div>

                            <div>
                              {matcher.strategy === 'boolean-map' ? (
                                <select
                                  className="waste-import-wizard__select"
                                  value={
                                    entry.targetValue === true
                                      ? 'true'
                                      : entry.targetValue === false
                                        ? 'false'
                                        : ''
                                  }
                                  onChange={(event) => {
                                    if (event.target.value === '') {
                                      handleValueMappingChange(
                                        matcher.fieldKey,
                                        entry.sourceValue,
                                        undefined,
                                      );
                                      return;
                                    }
                                    handleValueMappingChange(
                                      matcher.fieldKey,
                                      entry.sourceValue,
                                      event.target.value === 'true',
                                    );
                                  }}
                                >
                                  <option value="">Sin resolver</option>
                                  <option value="true">Sí</option>
                                  <option value="false">No</option>
                                </select>
                              ) : matcher.targetOptions ? (
                                <select
                                  className="waste-import-wizard__select"
                                  value={entry.targetValue === undefined ? '' : String(entry.targetValue)}
                                  onChange={(event) =>
                                    handleValueMappingChange(
                                      matcher.fieldKey,
                                      entry.sourceValue,
                                      event.target.value || undefined,
                                    )
                                  }
                                >
                                  <option value="">Sin resolver</option>
                                  {matcher.targetOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  className="waste-import-wizard__input"
                                  value={entry.targetValue === undefined ? '' : String(entry.targetValue)}
                                  onChange={(event) =>
                                    handleValueMappingChange(
                                      matcher.fieldKey,
                                      entry.sourceValue,
                                      event.target.value || undefined,
                                    )
                                  }
                                />
                              )}
                            </div>

                            <div>
                              {entry.targetValue === undefined || entry.targetValue === '' ? (
                                <span className="waste-import-wizard__pill is-error">
                                  Pendiente
                                </span>
                              ) : (
                                <span className="waste-import-wizard__pill is-success">
                                  Resuelto
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </section>
          )}

          {activeStep.id === 'preview' && (
            <section className="waste-import-wizard__section">
              {previewError && (
                <div className="waste-import-wizard__error-banner">{previewError}</div>
              )}

              {isPreviewLoading && (
                <div className="waste-import-wizard__info-banner">
                  <Loader2 size={16} className="waste-import-wizard__spin" />
                  Generando vista previa...
                </div>
              )}

              {preview && (
                <>
                  <div className="waste-import-wizard__summary-grid">
                    <div className="waste-import-wizard__summary-card">
                      <span>Total</span>
                      <strong>{preview.summary.totalRows}</strong>
                    </div>
                    <div className="waste-import-wizard__summary-card is-success">
                      <span>Válidas</span>
                      <strong>{preview.summary.validRows}</strong>
                    </div>
                    <div className="waste-import-wizard__summary-card is-warning">
                      <span>Con avisos</span>
                      <strong>{preview.summary.warningRows}</strong>
                    </div>
                    <div className="waste-import-wizard__summary-card is-danger">
                      <span>Inválidas</span>
                      <strong>{preview.summary.invalidRows}</strong>
                    </div>
                    <div className="waste-import-wizard__summary-card">
                      <span>Omitidas</span>
                      <strong>{preview.summary.omittedRows}</strong>
                    </div>
                  </div>

                  <div className="waste-import-wizard__card">
                    <h3>Resumen por estado</h3>
                    <div className="waste-import-wizard__pill-row">
                      {Object.entries(previewStatusCounts).map(([status, count]) => (
                        <span key={status} className="waste-import-wizard__pill">
                          {formatStatus(status)}: {count}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="waste-import-wizard__card">
                    <h3>Control de calidad espacial</h3>
                    <p className="waste-import-wizard__card-intro">
                      Detecta coincidencias exactas, puntos muy cercanos y filas fuera del área
                      válida del proyecto activo.
                    </p>
                    <div className="waste-import-wizard__summary-grid compact">
                      {previewSpatialQualityItems.map((item) => (
                        <div
                          key={item.key}
                          className={[
                            'waste-import-wizard__summary-card',
                            item.tone === 'danger' && item.count > 0
                              ? 'is-danger'
                              : item.tone === 'warning' && item.count > 0
                                ? 'is-warning'
                                : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span>{item.label}</span>
                          <strong>{item.count}</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="waste-import-wizard__card">
                    <h3>Decisiones de importación</h3>
                    <p className="waste-import-wizard__card-intro">
                      Puedes resolver en bloque duplicados, conflictos y proximidades. Las filas
                      fuera del área del proyecto siguen bloqueadas en esta fase.
                    </p>
                    <div className="waste-import-wizard__resolution-grid">
                      {previewResolutionItems.map((item) => (
                        <div
                          key={item.key}
                          className={[
                            'waste-import-wizard__resolution-card',
                            item.count > 0 ? 'is-active' : 'is-empty',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <div className="waste-import-wizard__resolution-head">
                            <div>
                              <strong>{item.label}</strong>
                              <p>{item.description}</p>
                            </div>
                            <span
                              className={[
                                'waste-import-wizard__pill',
                                item.count > 0 ? 'is-warning' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              {item.count} fila(s)
                            </span>
                          </div>
                          <label className="waste-import-wizard__resolution-control">
                            <span>Acción</span>
                            <select
                              className="waste-import-wizard__select"
                              value={item.action}
                              disabled={item.count === 0 || isPreviewLoading || isCommitLoading}
                              onChange={(event) => {
                                void handleQualityResolutionChange(
                                  item.key,
                                  event.target.value as 'keep' | 'omit',
                                );
                              }}
                            >
                              <option value="keep">{item.keepLabel}</option>
                              <option value="omit">{item.omitLabel}</option>
                            </select>
                          </label>
                        </div>
                      ))}

                      <div className="waste-import-wizard__resolution-card is-locked">
                        <div className="waste-import-wizard__resolution-head">
                          <div>
                            <strong>Fuera del área del proyecto</strong>
                            <p>
                              Las filas que queden fuera del área válida no se pueden importar ni
                              omitir automáticamente en esta fase.
                            </p>
                          </div>
                          <span className="waste-import-wizard__pill is-error">
                            {preview.summary.outsideProjectAreaRows ?? 0} fila(s)
                          </span>
                        </div>
                        <div className="waste-import-wizard__resolution-lock">
                          <span className="waste-import-wizard__pill is-error">
                            Siempre bloqueado
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="waste-import-wizard__table">
                    <div className="waste-import-wizard__table-head">
                      <span>Fila</span>
                      <span>Estado</span>
                      <span>Observaciones</span>
                      <span>Valores detectados</span>
                    </div>

                    {preview.rows.map((row) => {
                      const issueCounts = getIssueCounts(row.issues);
                      return (
                        <div
                          key={`preview-row-${row.sourceRowIndex}`}
                          className="waste-import-wizard__table-row"
                        >
                          <div>
                            <strong>#{row.sourceRowIndex + 1}</strong>
                          </div>
                          <div>
                            <span className="waste-import-wizard__pill">
                              {formatStatus(row.status)}
                            </span>
                          </div>
                          <div>
                            {row.issues.length === 0 ? (
                              <span className="waste-import-wizard__muted">Sin observaciones</span>
                            ) : (
                              <div className="waste-import-wizard__issue-stack">
                                <span className="waste-import-wizard__issue-summary">
                                  {summarizeIssueMessages(row.issues, 2)}
                                </span>
                                <div className="waste-import-wizard__issue-list">
                                  {getPriorityIssues(row.issues).map((issue) => (
                                    <span
                                      key={`${row.sourceRowIndex}-${issue.code}`}
                                      className={[
                                        'waste-import-wizard__pill',
                                        issue.category === 'error'
                                          ? 'is-error'
                                          : issue.category === 'warning'
                                            ? 'is-warning'
                                            : '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    >
                                      {formatIssueCodeLabel(issue)}
                                    </span>
                                  ))}
                                  {Object.entries(issueCounts)
                                    .filter(([, count]) => count > 0)
                                    .map(([category, count]) => (
                                      <span
                                        key={`${row.sourceRowIndex}-${category}`}
                                        className="waste-import-wizard__pill"
                                      >
                                        {formatIssueCategory(
                                          category as ImportPreviewIssue['category'],
                                        )}
                                        : {count}
                                      </span>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div>
                            <span className="waste-import-wizard__muted">
                              {formatMappedFields(row.mappedFields, activeDefinition)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          )}

          {activeStep.id === 'result' && (
            <section className="waste-import-wizard__section">
              {commitError && (
                <div className="waste-import-wizard__error-banner">{commitError}</div>
              )}

              {isCommitLoading && (
                <div className="waste-import-wizard__info-banner">
                  <Loader2 size={16} className="waste-import-wizard__spin" />
                  Persistiendo filas válidas...
                </div>
              )}

              {runDetails && (
                <>
                  <div className="waste-import-wizard__grid">
                    <div className="waste-import-wizard__card">
                      <h3>Resultado de la importación</h3>
                      <p className="waste-import-wizard__card-intro">
                        {buildResultHeadline(runDetails.summary, activeResultEntityPluralLabel)}
                      </p>
                      <dl className="waste-import-wizard__summary-list">
                        <div>
                          <dt>Entidad</dt>
                          <dd>{formatEntityLabel(runDetails.entityType, supportedEntities)}</dd>
                        </div>
                        <div>
                          <dt>Estado</dt>
                          <dd>{formatStatus(runDetails.status)}</dd>
                        </div>
                        <div>
                          <dt>Archivo</dt>
                          <dd>{runDetails.source.fileName ?? '—'}</dd>
                        </div>
                      </dl>
                      <div className="waste-import-wizard__technical-meta">
                        <span>Referencia técnica</span>
                        <div className="waste-import-wizard__technical-meta-row">
                          <code>{runDetails.runId}</code>
                          <button
                            type="button"
                            className="waste-import-wizard__text-button"
                            onClick={() => {
                              if (!navigator.clipboard) {
                                showToast('error', 'El navegador no permite copiar la referencia');
                                return;
                              }
                              void navigator.clipboard
                                .writeText(runDetails.runId)
                                .then(() => showToast('success', 'Referencia copiada'))
                                .catch(() =>
                                  showToast('error', 'No se pudo copiar la referencia'),
                                );
                            }}
                          >
                            <Copy size={14} />
                            Copiar
                          </button>
                        </div>
                      </div>
                    </div>

                  {runDetails.summary && (
                      <div className="waste-import-wizard__card">
                        <h3>Resumen final</h3>
                        <div className="waste-import-wizard__summary-grid">
                          <div className="waste-import-wizard__summary-card">
                            <span>Total</span>
                            <strong>{runDetails.summary.totalRows}</strong>
                          </div>
                          <div className="waste-import-wizard__summary-card is-success">
                            <span>Importadas</span>
                            <strong>{runDetails.summary.importedRows}</strong>
                          </div>
                          <div className="waste-import-wizard__summary-card is-warning">
                            <span>Con avisos</span>
                            <strong>{runDetails.summary.warningRows}</strong>
                          </div>
                          <div className="waste-import-wizard__summary-card">
                            <span>Con valores automáticos</span>
                            <strong>{runDetails.summary.rowsWithDefaults}</strong>
                          </div>
                          <div className="waste-import-wizard__summary-card is-danger">
                            <span>No importadas</span>
                            <strong>
                              {runDetails.summary.invalidRows +
                                runDetails.summary.failedRows +
                                runDetails.summary.omittedRows}
                            </strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {runDetails.summary && (
                    <div className="waste-import-wizard__card">
                      <h3>Control de calidad detectado</h3>
                      <p className="waste-import-wizard__card-intro">
                        Resume duplicados exactos, conflictos, proximidades y filas fuera del área
                        detectadas durante la vista previa.
                      </p>
                      <div className="waste-import-wizard__summary-grid compact">
                        {resultSpatialQualityItems.map((item) => (
                          <div
                            key={item.key}
                            className={[
                              'waste-import-wizard__summary-card',
                              item.tone === 'danger' && item.count > 0
                                ? 'is-danger'
                                : item.tone === 'warning' && item.count > 0
                                  ? 'is-warning'
                                  : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {runReport && (
                    <>
                      {resultRowsToReview.length > 0 && (
                        <div className="waste-import-wizard__card">
                          <h3>Filas a revisar</h3>
                          <ul className="waste-import-wizard__result-list">
                            {resultRowsToReview.map((row) => (
                              <li key={`review-row-${row.sourceRowIndex}`}>
                                <strong>Fila #{row.sourceRowIndex + 1}</strong>
                                <span>{summarizeIssueMessages(row.issues)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {resultRowsWithDefaults.length > 0 && (
                        <div className="waste-import-wizard__card">
                          <h3>Valores completados automáticamente</h3>
                          {resultRowsWithDefaults.length > 6 && (
                            <p className="waste-import-wizard__card-intro">
                              Mostrando las primeras 6 de {resultRowsWithDefaults.length} fila(s)
                              con valores automáticos.
                            </p>
                          )}
                          <ul className="waste-import-wizard__result-list">
                            {resultRowsWithDefaults.slice(0, 6).map((row) => (
                              <li key={`default-row-${row.sourceRowIndex}`}>
                                <strong>Fila #{row.sourceRowIndex + 1}</strong>
                                <span>
                                  {summarizeIssueMessages(
                                    row.issues.filter(
                                      (issue) => issue.category === 'default_applied',
                                    ),
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {resultRowsWithWarnings.length > 0 && (
                        <div className="waste-import-wizard__card">
                          <h3>Importadas con observaciones</h3>
                          {resultRowsWithWarnings.length > 6 && (
                            <p className="waste-import-wizard__card-intro">
                              Mostrando las primeras 6 de {resultRowsWithWarnings.length} fila(s)
                              con observaciones.
                            </p>
                          )}
                          <ul className="waste-import-wizard__result-list">
                            {resultRowsWithWarnings.slice(0, 6).map((row) => (
                              <li key={`warning-row-${row.sourceRowIndex}`}>
                                <strong>Fila #{row.sourceRowIndex + 1}</strong>
                                <span>
                                  {summarizeIssueMessages(
                                    row.issues.filter(
                                      (issue) => issue.category === 'warning',
                                    ),
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="waste-import-wizard__card">
                        <h3>Resumen por estado de fila</h3>
                        <div className="waste-import-wizard__pill-row">
                          {Object.entries(resultStatusCounts).map(([status, count]) => (
                            <span key={status} className="waste-import-wizard__pill">
                              {formatStatus(status)}: {count}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="waste-import-wizard__table">
                        <div className="waste-import-wizard__table-head">
                          <span>Fila</span>
                          <span>Estado</span>
                          <span>Registro creado</span>
                          <span>Detalle</span>
                        </div>

                        {runReport.rows.map((row) => (
                          <div
                            key={`result-row-${row.sourceRowIndex}`}
                            className="waste-import-wizard__table-row"
                          >
                            <div>
                              <strong>#{row.sourceRowIndex + 1}</strong>
                            </div>
                            <div>
                              <span className="waste-import-wizard__pill">
                                {formatStatus(row.status)}
                              </span>
                            </div>
                            <div>
                              {row.persistedEntityId ? (
                                <div className="waste-import-wizard__sample-block">
                                  <span>{row.persistedCode ?? 'Sin código visible'}</span>
                                  <small>{row.persistedEntityId}</small>
                                </div>
                              ) : (
                                <span className="waste-import-wizard__muted">No se creó el registro</span>
                              )}
                            </div>
                            <div>
                              {row.issues.length === 0 ? (
                                <span className="waste-import-wizard__muted">Sin observaciones</span>
                              ) : (
                                <ul className="waste-import-wizard__inline-list">
                                  {row.issues.slice(0, 3).map((issue, issueIndex) => (
                                    <li key={`${row.sourceRowIndex}-${issue.code}-${issueIndex}`}>
                                      <strong>{formatIssueCategory(issue.category)}:</strong>{' '}
                                      {issue.message}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </section>
          )}
        </div>

        <div className="waste-import-wizard__footer">
          {activeStep.id === 'result' ? (
            <button
              type="button"
              className="waste-import-wizard__button is-primary"
              onClick={onClose}
            >
              Cerrar
            </button>
          ) : (
            <>
              <div className="waste-import-wizard__footer-left">
                <button
                  type="button"
                  className="waste-import-wizard__button"
                  onClick={onClose}
                  disabled={isPreviewLoading || isCommitLoading}
                >
                  Cancelar
                </button>
              </div>

              <div className="waste-import-wizard__footer-right">
                {activeStepIndex > 0 && (
                  <button
                    type="button"
                    className="waste-import-wizard__button"
                    onClick={handleBack}
                    disabled={isPreviewLoading || isCommitLoading}
                  >
                    <ArrowLeft size={16} />
                    Atrás
                  </button>
                )}

                <button
                  type="button"
                  className="waste-import-wizard__button is-primary"
                  onClick={() => {
                    void handleNext();
                  }}
                  disabled={!canGoNext || isPreviewLoading || isCommitLoading}
                >
                  {isPreviewLoading || isCommitLoading ? (
                    <Loader2 size={16} className="waste-import-wizard__spin" />
                  ) : activeStep.id === 'value-matching' ? (
                    'Generar vista previa'
                  ) : activeStep.id === 'preview' ? (
                    'Confirmar importación'
                  ) : (
                    <>
                      Continuar
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
